import classNames from "classnames"

import ImageLink from "@components/elements/ImageLink"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Section from "@components/elements/V2/Section"
import TrustedStamp from "@components/elements/V2/TrustedStamp"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/elements/V2/Video"
import HeroLayout from "@components/V2/Heroes/HeroLayout"
import { Color } from "@constants/V2/color"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React from "react"

type Props = {
  headingSuperscript?: string
  heading?: string
  headingSize?: "medium" | "large"
  description?: string
  highlightedDescriptionText?: string
  imageUrl: string
  imageAlt: string
  products?: (
    | YocoCom.ConsolidatedProductItem
    | YocoCom.ConsolidatedBundleItem
  )[]
  videoUrl?: string
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
  backgroundColor?: Color
  imageButtonDescription?: string
  linkToPurchase?: boolean
}

const ThirdsHero = ({
  headingSuperscript,
  heading,
  headingSize = "large",
  description,
  highlightedDescriptionText,
  imageUrl,
  imageAlt,
  products,
  videoUrl,
  ctaText,
  ctaLink,
  backgroundColor = Color.White,
  linkToPurchase = false,
  ...props
}: Props) => {
  return (
    <HeroLayout>
      <Section
        className={classNames(
          "grid grid-cols-12 pt-12 sm:pb-40 md:pt-navHeight-md",
          getBackgroundColorClass(backgroundColor)
        )}
        {...props}
      >
        <div className="col-span-12 flex flex-col gap-10 pt-[36px] md:col-span-4 md:pr-[52px] xl:pt-[112px]">
          <Typography
            text={(headingSuperscript ?? "").toLocaleUpperCase()}
            size="body-sm"
            font="grotesk"
            weight="book"
            color={getAccentColor(backgroundColor)}
          />

          <Typography
            text={heading ?? ""}
            size={headingSize === "large" ? "h4" : "h5"}
            font="grotesk"
            weight="medium"
            color={getAccentColor(backgroundColor)}
          />

          <Typography
            text={description ?? ""}
            size="body-md"
            font="grotesk"
            weight="book"
            color={getAccentColor(backgroundColor)}
            highlightedText={highlightedDescriptionText}
            highlightColor={getAccentColor(backgroundColor)}
            highlightWeight="medium"
          />

          {ctaText && ctaLink && (
            <PillButton
              text={ctaText}
              style="solid"
              size="medium"
              color={Color.Blue}
              linkUrl={ctaLink}
              className="mt-32 w-auto sm:!w-[204px]"
            />
          )}

          <div className="my-24 flex w-full flex-wrap gap-16">
            {products?.map((product) => {
              const productLink = linkToPurchase
                ? product.ctaLink
                : product.detailsLink

              if (!productLink) return
              return (
                <ImageLink
                  key={`product-${product.name}-cta`}
                  link={productLink}
                  imageUrl={product.thumbnail?.filename}
                  imageAlt={product.thumbnail?.alt}
                  text={product.name}
                  description={
                    product.type === "product"
                      ? product.imageButtonDescription
                      : undefined
                  }
                  size="medium"
                />
              )
            })}
          </div>

          <TrustedStamp
            className={classNames({
              "mt-auto": products?.length && products.length > 0,
            })}
          />
        </div>

        <div className="col-span-12 my-[36px] md:col-span-8 md:my-0">
          {videoUrl ? (
            <Video
              video={videoUrl}
              coverImage={imageUrl}
              ImageAlt={imageAlt}
              loadType="eager"
              aspectRatio="4:3"
              playbackMode="autoplay"
              playOnMobile
              className="rounded-12"
            />
          ) : (
            <GatsbyStoryblokImage
              image={imageUrl}
              alt={imageAlt}
              loadType="eager"
              fetchPriority="high"
              placeholderColor={backgroundColor}
              quality={80}
              aspectRatio="fullWidth"
              rounded
              className="h-full w-auto"
              sizes="(min-width: 1512px) 54vw, (min-width: 1200px) 57vw, (min-width: 900px) 60vw, calc(100vw - 48px)"
              srcSetWidths={[480, 640, 768, 1024, 1200, 1600, 2048]}
            />
          )}
        </div>
      </Section>
    </HeroLayout>
  )
}

export default ThirdsHero
